import { useEffect, useRef, useState } from 'react';
import { Row, Col, Radio, RadioChangeEvent, Button, Form, Input, message } from 'antd';
import Images from '../assets/image/index';
import { GooglePlusOutlined } from "@ant-design/icons";
import { useRegister, useSendVerificationCode } from '../services/user.service';
import IResponse from '../types/response';
import { useNavigate } from 'react-router-dom';
import { storage } from '../utils/storage';
import 'react-phone-input-2/lib/style.css'
import { isNotNullUndefinedBlank, onAutoGeneratePassword } from '../helpers/common';
import { googleAuthenticateUrl } from '../helpers/socialauthenticateurl';
import { checkPasswordStrength } from '../helpers/passwordstrength';

const Registration = () => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [userRoleId, setUserRoleId] = useState(3);
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const isEmailPhoneNumberRequired = useRef(false);
    const [isPasswordRequired, setIsPasswordRequired] = useState(false);
    const [passwordScore, setPasswordScore] = useState<number>(0);
    const [password, setPassword] = useState('');

    useEffect(() => {
        const storedEmail = storage.getEmailId() ?? "";
        setEmail(storedEmail);
        form.setFieldsValue({ email: storedEmail });
        if (isNotNullUndefinedBlank(storedEmail)) {
            setIsPasswordRequired(true);
            storage.clearToken();
            storage.clearRefreshToken();
            storage.clearUserRoleId();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onChange = (e: RadioChangeEvent) => {
        setUserRoleId(parseInt(e.target.value));
    };

    const inputChange = (event: any) => {
        const { value } = event.target;
        setPasswordScore(checkPasswordStrength(value));
        form.setFieldValue("password", value);
        setPassword(value);
    };

    const onChangeInputValue = (event: any, values: string = "") => {
        const value = isNotNullUndefinedBlank(values) ? values : event.target.value;
        if (value.length >= 3) {
            if (/^[0-9]+$/.test(value)) {
                setEmail('');
                setIsPasswordRequired(false);
            } else {
                setEmail(value);
                setIsPasswordRequired(true);
            }
        } else {
            // covers both length === 0 AND 1–2 chars
            setEmail('');
            setIsPasswordRequired(false);
        }
    };

    const sendVerificationCode = async () => {
        sendVerificationCodeMutation.mutate({
            userEmail: email,
            phoneNumber: "",
            phoneNoDialingCode: ""
        });
    };

    const sendVerificationCodeMutation = useSendVerificationCode({
        onSuccess: (response: any) => {
            if (response.statusCode === 200) {
                if (isNotNullUndefinedBlank(email)) {
                    storage.setCodeType("email");
                    storage.setEmailId(email);
                }
                message.success("OTP has been sent. Please check your email.");
                navigate('/authorization');
            } else {
                message.error(response.statusMessage);
            }
        },
        onError: (error) => {
            message.error(error.message);
        }
    });

    const onRegister = async (values: any) => {
        if (!isNotNullUndefinedBlank(email)) {
            isEmailPhoneNumberRequired.current = true;
            message.error("Please enter a valid email address.");
            return;
        }
        isEmailPhoneNumberRequired.current = false;
        register.mutate({
            userRoleId: userRoleId,
            email: email,
            password: values.password,
            phoneNoDialingCode: "",
        });
        setLoading(true);
        storage.setPreviousUrlPath(window.location.pathname);
    };

    const register = useRegister({
        onSuccess: (response: IResponse) => {
            setLoading(false);
            if (response.statusCode === 200) {
                message.success('Registration complete! Sending your verification code...');
                if (isNotNullUndefinedBlank(email)) {
                    sendVerificationCode();
                } else {
                    navigate("/completeprofile");
                }
            } else if (response.statusMessage !== "") {
                message.error(response.statusMessage);
                if (email !== "") storage.setEmailId(email);
            } else {
                message.error("Registration failed. Please try again.");
            }
        },
        onError: (error) => {
            setLoading(false);
            message.error(error.message);
        }
    });

    const registerWithGoogle = () => {
        storage.setUserRoleId(userRoleId);
        window.location.href = googleAuthenticateUrl();
    };

    const getAutoGeneratePassword = () => {
        const pw = onAutoGeneratePassword();
        form.setFieldsValue({ password: pw, confirmPassword: pw });
        setPassword(pw);
        setPasswordScore(checkPasswordStrength(pw));
    };

    return (
        <div className="app-container">
            <Row className="min-h-screen">
                <Col span={12} className="hidden lg:block bg-black h-screen sticky top-0">
                    <div className='h-screen bg-login-background bg-no-repeat bg-cover flex justify-center items-center'>
                        <div>
                            <div className='flex justify-center w-full'>
                                <div className='w-72'>
                                    <img src={Images.triplogo} className='w-full' alt="triplogo" />
                                </div>
                            </div>
                            <div className='mt-20 flex justify-center'>
                                <img src={Images.travellogo} className='w-[335px] 2xl:w-[443px]' alt="travel-logo" />
                            </div>
                        </div>
                    </div>
                </Col>
                <Col span={24} lg={12}>
                    <div className='block lg:w-0 lg:h-0 lg:hidden'>
                        <div className='flex justify-center py-16'>
                            <img src={Images.mobilelogo} className='w-[140px] h-[60px] lg:w-[219px] lg:h-[96px]' alt="triplogo" />
                        </div>
                    </div>
                    <div className='flex justify-center items-center lg:h-screen'>
                        <div className="p-6 w-full max-w-md">
                            <div>
                                <p className="text-center text-[32px] font-bold text-black">Create an account</p>
                                <p className='mb-6 text-center text-base font-medium'>Let's get you connected</p>
                                <div className='flex mb-3'>
                                    <p className='font-semibold mr-5'>I am</p>
                                    <Radio.Group onChange={onChange} value={userRoleId}>
                                        <Radio value={3} className='font-semibold'>Traveller</Radio>
                                        <Radio value={2} className='font-semibold'>Trip Assistant</Radio>
                                    </Radio.Group>
                                </div>
                                <Form
                                    layout="vertical"
                                    form={form}
                                    initialValues={{ email: '', password: '', confirmPassword: '' }}
                                    onFinish={onRegister}
                                >
                                    {/* Email */}
                                    <Form.Item
                                        className="font-semibold"
                                        label="Email"
                                        name="email"
                                        rules={[
                                            { required: true, message: "Please enter your email address." },
                                            { type: "email", message: "Please enter a valid email address." },
                                        ]}
                                        validateTrigger={["onBlur", "onChange"]}
                                    >
                                        <Input
                                            onChange={onChangeInputValue}
                                            autoFocus
                                            className="font-medium text-neutral-600 h-12"
                                            placeholder="Enter your email"
                                        />
                                    </Form.Item>

                                    {/* Password — only shown when email is valid */}
                                    {isPasswordRequired && (
                                        <>
                                            <Form.Item
                                                className="font-semibold auto-password"
                                                label={
                                                    <div className="flex justify-between w-full">
                                                        <span>Password</span>
                                                        <button
                                                            type="button"
                                                            className="underline cursor-pointer text-black text-xs border-none bg-transparent hover:text-black"
                                                            onClick={getAutoGeneratePassword}
                                                        >
                                                            Auto Generate Password
                                                        </button>
                                                    </div>
                                                }
                                                name="password"
                                                rules={[
                                                    { required: true, message: 'Please enter a password.' },
                                                    { min: 6, message: 'Password must be at least 6 characters.' },
                                                ]}
                                                validateTrigger={["onBlur", "onSubmit"]}
                                            >
                                                <Input.Password
                                                    className="font-medium text-neutral-600 h-12"
                                                    placeholder="Enter your password"
                                                    value={password}
                                                    onChange={inputChange}
                                                />
                                            </Form.Item>

                                            {/* Password strength bar */}
                                            <div className="flex -mx-1 mb-3">
                                                {Array.from({ length: 5 }).map((_, i) => (
                                                    <div key={i} className="w-1/5 px-1">
                                                        <div className={`h-1 rounded-xl transition-colors ${i < passwordScore
                                                            ? passwordScore <= 2 ? "bg-red-600"
                                                                : passwordScore <= 3 ? "bg-orange-400"
                                                                    : passwordScore <= 4 ? "bg-yellow-400"
                                                                        : "bg-green-500"
                                                            : "bg-slate-300"}`} />
                                                    </div>
                                                ))}
                                            </div>
                                            {passwordScore > 0 && (
                                                <div className='text-end -mt-2 mb-3'>
                                                    <span className={`text-xs font-semibold ${passwordScore <= 2 ? 'text-red-600' : passwordScore <= 3 ? 'text-orange-400' : passwordScore <= 4 ? 'text-yellow-400' : 'text-green-500'}`}>
                                                        {passwordScore <= 2 ? 'Low' : passwordScore <= 3 ? 'Weak' : passwordScore <= 4 ? 'Medium' : 'Strong'}
                                                    </span>
                                                </div>
                                            )}

                                            {/* Confirm Password */}
                                            <Form.Item
                                                className="font-semibold"
                                                label="Confirm Password"
                                                name="confirmPassword"
                                                dependencies={['password']}
                                                rules={[
                                                    { required: true, message: 'Please confirm your password.' },
                                                    ({ getFieldValue }) => ({
                                                        validator(_, value) {
                                                            if (!value || getFieldValue('password') === value) {
                                                                return Promise.resolve();
                                                            }
                                                            return Promise.reject(new Error('Passwords do not match.'));
                                                        },
                                                    }),
                                                ]}
                                                validateTrigger={["onBlur", "onChange"]}
                                            >
                                                <Input.Password
                                                    className="font-medium text-neutral-600 h-12"
                                                    placeholder="Re-enter your password"
                                                />
                                            </Form.Item>
                                        </>
                                    )}

                                    <Form.Item>
                                        <Button
                                            htmlType='submit'
                                            loading={loading}
                                            className="h-14 w-full mt-4 text-xl bg-black text-white hover:!border-black hover:!bg-transparent hover:!text-black hover:transition hover:duration-500 hover:ease-in-out cursor-pointer"
                                        >
                                            Create an account
                                        </Button>
                                    </Form.Item>

                                    <p className='text-center text-base mt-2'>Or continue with</p>
                                    <div className="mt-4">
                                        <Button
                                            type="primary"
                                            className="bg-red-50 w-full h-10 text-black font-sora text-base font-semibold py-3.5 flex justify-center items-center outline-none hover:!border-black hover:!bg-transparent hover:!text-black hover:transition hover:duration-500 hover:ease-in-out cursor-pointer"
                                            onClick={registerWithGoogle}
                                        >
                                            <GooglePlusOutlined />
                                            <span>Google</span>
                                        </Button>
                                    </div>

                                    <div className="mt-8">
                                        <p className="text-base font-normal text-center text-black-russian">
                                            Have an Admin account?{' '}
                                            <a href="/" className="outline-none cursor-pointer" onClick={() => storage.clearPreviousUrlPath()}>
                                                <span className="text-xs sm:text-base text-black font-semibold underline">Sign in!</span>
                                            </a>
                                        </p>
                                    </div>
                                </Form>
                            </div>
                        </div>
                    </div>
                </Col>
            </Row>
        </div>
    );
}

export default Registration;
